/**
 * SVG overlay for pen / highlighter / engineering markup on PDF pages and images.
 * Coordinates 0–100 match AnnotationLayer / comment pins.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { DocumentMarkupPoint, DocumentMarkupStroke, DocumentMarkupTool } from '../../types';
import {
  ENG_CALLOUT_FILL,
  ERASER_HIT,
  calloutBoxPx,
  cloudRect,
  dist,
  finishLinePoints,
  hitStampHandle,
  isEngineeringMarkupTool,
  isContinuousDrawTool,
  isMarkupTapGesture,
  isShapeMarkupTool,
  isStoredFreehandTool,
  markupHitsPoint,
  orthogonalElbow,
  pxAngle,
  pxDist,
  stampHalfPx,
  type StampHandle,
} from './engineeringMarkup';
import { getFastener } from './fastenerLibrary';
import {
  pushMarkupHistory,
  remapHistoryIds,
  strokeToCommitPayload,
  type MarkupCommitPayload,
  type MarkupHistoryOp,
  type MarkupStrokePatch,
} from './markupHistory';
import { DraftMarkupShape, MarkupShape, SelectedMarkupHalo, StampTransformBox } from './MarkupShapes';
import { resolveMarkupStrokeWidth } from './markupStrokeWidth';
import './MarkupLayer.css';

export type { MarkupCommitPayload, MarkupStrokePatch };

const MIN_POINT_DIST = 0.2;
const MAX_POINTS = 600;

interface CalloutEditor {
  tool: 'callout' | 'typewriter';
  points: DocumentMarkupPoint[];
  text: string;
  strokeId?: string;
  screen: { x: number; y: number };
}

interface StampXform {
  id: string;
  kind: 'move' | 'scale' | 'rotate';
  origin: DocumentMarkupPoint;
  start: DocumentMarkupPoint;
  origPoint: DocumentMarkupPoint;
  origScale: number;
  origRot: number;
}

export interface MarkupLayerProps {
  pageNumber: number;
  scale: number;
  strokes: DocumentMarkupStroke[];
  activeTool: DocumentMarkupTool;
  color: string;
  baseStrokeWidth: number;
  enabled: boolean;
  currentUserId?: string;
  onCommitStroke: (payload: MarkupCommitPayload) => Promise<string | void>;
  onDeleteStroke: (strokeId: string) => Promise<void>;
  onUpdateStroke?: (strokeId: string, patch: MarkupStrokePatch) => Promise<void>;
  selectedStampId?: string;
  canEraseAnyEngineering?: boolean;
}

function mergeLive(stroke: DocumentMarkupStroke, live: StampXform | null, pointer: DocumentMarkupPoint | null, svg: { w: number; h: number }): DocumentMarkupStroke {
  if (!live || live.id !== stroke.id || !pointer) return stroke;
  if (live.kind === 'move') {
    return {
      ...stroke,
      points: [{ x: live.origPoint.x + (pointer.x - live.start.x), y: live.origPoint.y + (pointer.y - live.start.y) }],
    };
  }
  if (live.kind === 'scale') {
    const startD = pxDist(live.origPoint, live.start, svg.w, svg.h);
    const nowD = pxDist(live.origPoint, pointer, svg.w, svg.h);
    const next = startD < 2 ? live.origScale : live.origScale * (nowD / startD);
    return { ...stroke, stampScale: Math.max(0.3, Math.min(6, next)) };
  }
  const startAng = pxAngle(live.origPoint, live.start, svg.w, svg.h);
  const nowAng = pxAngle(live.origPoint, pointer, svg.w, svg.h);
  return { ...stroke, stampRotation: live.origRot + (nowAng - startAng) };
}

export function MarkupLayer({
  pageNumber,
  scale,
  strokes,
  activeTool,
  color,
  baseStrokeWidth,
  enabled,
  currentUserId,
  onCommitStroke,
  onDeleteStroke,
  onUpdateStroke,
  selectedStampId = 'peg',
  canEraseAnyEngineering = false,
}: MarkupLayerProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [svgSize, setSvgSize] = useState({ w: 800, h: 600 });
  const [draft, setDraft] = useState<DocumentMarkupPoint[] | null>(null);
  const [editor, setEditor] = useState<CalloutEditor | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverHandle, setHoverHandle] = useState<StampHandle | null>(null);
  const [liveXform, setLiveXform] = useState<StampXform | null>(null);
  const [livePointer, setLivePointer] = useState<DocumentMarkupPoint | null>(null);
  const draftRef = useRef<DocumentMarkupPoint[] | null>(null);
  const drawingRef = useRef(false);
  const shiftRef = useRef(false);
  const startRef = useRef<DocumentMarkupPoint | null>(null);
  const polyPinnedRef = useRef<DocumentMarkupPoint[]>([]);
  const editorTextRef = useRef('');
  const xformRef = useRef<StampXform | null>(null);
  const undoRef = useRef<MarkupHistoryOp[]>([]);
  const redoRef = useRef<MarkupHistoryOp[]>([]);
  const replayRef = useRef(false);
  const selectedIdRef = useRef<string | null>(null);
  const pageStrokesRef = useRef<DocumentMarkupStroke[]>([]);
  const commitRef = useRef(onCommitStroke);
  const deleteRef = useRef(onDeleteStroke);
  const updateRef = useRef(onUpdateStroke);

  const pageStrokes = strokes.filter(s => s.pageNumber === pageNumber);
  selectedIdRef.current = selectedId;
  pageStrokesRef.current = pageStrokes;
  commitRef.current = onCommitStroke;
  deleteRef.current = onDeleteStroke;
  updateRef.current = onUpdateStroke;
  const pageScale = { sx: 100 / Math.max(svgSize.w, 1), sy: 100 / Math.max(svgSize.h, 1) };

  const displayStrokes = pageStrokes.map(s => mergeLive(s, liveXform, livePointer, svgSize));
  const selectedAny = displayStrokes.find(s => s.id === selectedId) || null;
  const selectedStamp = selectedAny?.tool === 'stamp' ? selectedAny : null;

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setSvgSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const toNorm = useCallback((e: { currentTarget: SVGSVGElement; clientX: number; clientY: number }) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
  }, []);

  const screenForPoint = useCallback((p: DocumentMarkupPoint) => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: rect.left + (p.x / 100) * rect.width,
      y: rect.top + (p.y / 100) * rect.height,
    };
  }, []);

  const openEditor = useCallback((next: CalloutEditor) => {
    editorTextRef.current = next.text;
    setEditor(next);
  }, []);

  const canEditStroke = useCallback(
    (s: DocumentMarkupStroke) => canEraseAnyEngineering || s.userId === currentUserId,
    [canEraseAnyEngineering, currentUserId]
  );

  const canEraseStroke = useCallback(
    (s: DocumentMarkupStroke) => {
      if (!s.id) return false;
      if (isEngineeringMarkupTool(s.tool) || isShapeMarkupTool(s.tool)) return canEditStroke(s);
      return s.userId === currentUserId;
    },
    [canEditStroke, currentUserId]
  );

  const hitMarkupAt = useCallback(
    (p: DocumentMarkupPoint): DocumentMarkupStroke | null => {
      let best: { stroke: DocumentMarkupStroke; d: number } | null = null;
      for (const s of displayStrokes) {
        if (!s.id || !canEraseStroke(s)) continue;
        if (s.tool === 'stamp' && selectedId === s.id && hitStampHandle(p, s, svgSize.w, svgSize.h)) return s;
        const d = markupHitsPoint(p, s, svgSize.w, svgSize.h);
        if (d <= ERASER_HIT && (!best || d < best.d)) best = { stroke: s, d };
      }
      return best?.stroke ?? null;
    },
    [displayStrokes, canEraseStroke, selectedId, svgSize.w, svgSize.h]
  );

  const recordHistory = useCallback((op: MarkupHistoryOp) => {
    if (replayRef.current) return;
    pushMarkupHistory(undoRef.current, op);
    redoRef.current = [];
  }, []);

  const recordAdd = useCallback(
    (id: string | void, payload: MarkupCommitPayload) => {
      if (typeof id === 'string' && id) recordHistory({ type: 'add', id, payload });
    },
    [recordHistory]
  );

  const removeRecorded = useCallback(
    async (strokeId: string) => {
      const stroke = pageStrokesRef.current.find(s => s.id === strokeId) || strokes.find(s => s.id === strokeId);
      const payload = stroke ? strokeToCommitPayload(stroke) : null;
      await deleteRef.current(strokeId);
      if (selectedIdRef.current === strokeId) setSelectedId(null);
      if (payload) recordHistory({ type: 'remove', id: strokeId, payload });
    },
    [recordHistory, strokes]
  );

  const adoptRestoredId = useCallback((op: MarkupHistoryOp, nextId: string | void) => {
    if (typeof nextId !== 'string' || !nextId || nextId === op.id) return;
    remapHistoryIds(undoRef.current, op.id, nextId);
    remapHistoryIds(redoRef.current, op.id, nextId);
    op.id = nextId;
  }, []);

  const persistXform = useCallback(
    async (session: StampXform, pointer: DocumentMarkupPoint) => {
      const base = pageStrokes.find(s => s.id === session.id);
      if (!base || !onUpdateStroke) return;
      const next = mergeLive(base, session, pointer, svgSize);
      const after = {
        points: next.points,
        stampScale: next.stampScale,
        stampRotation: next.stampRotation,
      };
      await onUpdateStroke(session.id, after);
      recordHistory({
        type: 'update',
        id: session.id,
        before: {
          points: [session.origPoint],
          stampScale: session.origScale,
          stampRotation: session.origRot,
        },
        after,
      });
    },
    [pageStrokes, onUpdateStroke, svgSize, recordHistory]
  );

  const clearLineDraft = useCallback(() => {
    polyPinnedRef.current = [];
    drawingRef.current = false;
    draftRef.current = null;
    startRef.current = null;
    setDraft(null);
  }, []);

  const commitLinePoints = useCallback(
    async (live?: DocumentMarkupPoint | null) => {
      const pts = finishLinePoints(polyPinnedRef.current, live);
      clearLineDraft();
      if (!pts || !currentUserId) return;
      const payload: MarkupCommitPayload = {
        pageNumber,
        tool: 'line',
        color,
        strokeWidth: resolveMarkupStrokeWidth('line', baseStrokeWidth, scale),
        points: pts,
      };
      const id = await onCommitStroke(payload);
      recordAdd(id, payload);
      return id;
    },
    [clearLineDraft, currentUserId, pageNumber, color, baseStrokeWidth, scale, onCommitStroke, recordAdd]
  );

  const handlePointerDown = useCallback(
    async (e: React.PointerEvent<SVGSVGElement>) => {
      if (!enabled || editor) return;
      const p = toNorm(e);
      shiftRef.current = e.shiftKey;
      startRef.current = p;

      if (activeTool === 'eraser') {
        e.currentTarget.setPointerCapture(e.pointerId);
        let best: { id: string; d: number } | null = null;
        for (const s of pageStrokes) {
          if (!canEraseStroke(s)) continue;
          const d = markupHitsPoint(p, s, svgSize.w, svgSize.h);
          if (d <= ERASER_HIT && (!best || d < best.d)) best = { id: s.id!, d };
        }
        if (best) {
          await removeRecorded(best.id);
        }
        return;
      }

      if (activeTool === 'line' && drawingRef.current && polyPinnedRef.current.length > 0) {
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }

      if (selectedStamp) {
        const handle = hitStampHandle(p, selectedStamp, svgSize.w, svgSize.h);
        if (handle) {
          e.currentTarget.setPointerCapture(e.pointerId);
          const session: StampXform = {
            id: selectedStamp.id!,
            kind: handle === 'rotate' ? 'rotate' : handle === 'body' ? 'move' : 'scale',
            origin: selectedStamp.points[0],
            start: p,
            origPoint: selectedStamp.points[0],
            origScale: selectedStamp.stampScale ?? 1,
            origRot: selectedStamp.stampRotation ?? 0,
          };
          xformRef.current = session;
          setLiveXform(session);
          setLivePointer(p);
          return;
        }
      }

      // Pen / highlighter / line always start a new stroke. Selecting a mark
      // is a click (no drag), resolved on pointer-up — otherwise the just-drawn
      // line steals the next down and drawing stops.
      if (!isContinuousDrawTool(activeTool)) {
        const hit = hitMarkupAt(p);
        if (hit?.id) {
          if ((hit.tool === 'callout' || hit.tool === 'typewriter') && selectedId === hit.id) {
            const box = hit.tool === 'typewriter' ? hit.points[0] : hit.points[hit.points.length - 1] || hit.points[0];
            openEditor({
              tool: hit.tool,
              points: hit.points,
              text: hit.text || '',
              strokeId: hit.id,
              screen: screenForPoint(box),
            });
            return;
          }
          setSelectedId(hit.id);
          if (hit.tool === 'stamp') {
            e.currentTarget.setPointerCapture(e.pointerId);
            const session: StampXform = {
              id: hit.id,
              kind: 'move',
              origin: hit.points[0],
              start: p,
              origPoint: hit.points[0],
              origScale: hit.stampScale ?? 1,
              origRot: hit.stampRotation ?? 0,
            };
            xformRef.current = session;
            setLiveXform(session);
            setLivePointer(p);
          }
          return;
        }
      }

      setSelectedId(null);
      e.currentTarget.setPointerCapture(e.pointerId);
      drawingRef.current = true;
      draftRef.current = [p];
      setDraft([p]);
      if (activeTool === 'line') polyPinnedRef.current = [p];
    },
    [
      enabled,
      editor,
      activeTool,
      pageStrokes,
      canEraseStroke,
      toNorm,
      removeRecorded,
      svgSize.w,
      svgSize.h,
      selectedId,
      selectedStamp,
      hitMarkupAt,
      openEditor,
      screenForPoint,
    ]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<SVGSVGElement>) => {
      if (!enabled || editor) return;
      const p = toNorm(e);
      shiftRef.current = e.shiftKey;

      if (xformRef.current) {
        setLivePointer(p);
        return;
      }

      if (selectedStamp && !drawingRef.current) {
        setHoverHandle(hitStampHandle(p, selectedStamp, svgSize.w, svgSize.h));
      } else {
        setHoverHandle(null);
      }

      if (!drawingRef.current || activeTool === 'eraser') return;
      const cur = draftRef.current;
      if (!cur || cur.length >= MAX_POINTS) return;

      if (activeTool === 'stamp') {
        draftRef.current = [startRef.current || p];
        setDraft(draftRef.current);
        return;
      }

      if (activeTool === 'line') {
        const pinned = polyPinnedRef.current.length ? polyPinnedRef.current : [startRef.current || p];
        draftRef.current = [...pinned, p];
        setDraft(draftRef.current);
        return;
      }

      if (activeTool === 'callout') {
        const start = startRef.current || cur[0];
        if (shiftRef.current) {
          const elbow = orthogonalElbow(start, p);
          draftRef.current = [start, elbow, p];
        } else {
          draftRef.current = [start, p];
        }
        setDraft(draftRef.current);
        return;
      }

      if (activeTool === 'cloud') {
        const start = startRef.current || cur[0];
        draftRef.current = [start, p];
        setDraft(draftRef.current);
        return;
      }

      if (activeTool === 'typewriter') {
        draftRef.current = [startRef.current || p];
        setDraft(draftRef.current);
        return;
      }

      const last = cur[cur.length - 1];
      if (Math.hypot(p.x - last.x, p.y - last.y) < MIN_POINT_DIST) return;
      const next = [...cur, p];
      draftRef.current = next;
      setDraft(next);
    },
    [enabled, editor, activeTool, toNorm, selectedStamp, svgSize.w, svgSize.h]
  );

  const finishCalloutEditor = useCallback(
    async (text: string, cancel: boolean) => {
      const current = editor;
      setEditor(null);
      if (!current || cancel) return;
      const trimmed = text.trim();
      if (current.strokeId) {
        await onUpdateStroke?.(current.strokeId, { text: trimmed });
        if (current.text !== trimmed) {
          recordHistory({
            type: 'update',
            id: current.strokeId,
            before: { text: current.text },
            after: { text: trimmed },
          });
        }
        return;
      }
      if (!currentUserId) return;
      if (current.tool === 'typewriter') {
        const origin = current.points[0];
        if (!origin || !trimmed) return;
        const payload: MarkupCommitPayload = {
          pageNumber,
          tool: 'typewriter',
          color,
          strokeWidth: resolveMarkupStrokeWidth('typewriter', baseStrokeWidth, scale),
          points: [origin],
          text: trimmed,
          textColor: color,
        };
        const id = await onCommitStroke(payload);
        recordAdd(id, payload);
        if (id) setSelectedId(id);
        return;
      }
      if (current.points.length < 2) return;
      const payload: MarkupCommitPayload = {
        pageNumber,
        tool: 'callout',
        color,
        strokeWidth: resolveMarkupStrokeWidth('callout', baseStrokeWidth, scale),
        points: current.points,
        text: trimmed,
        fill: ENG_CALLOUT_FILL,
        textColor: color,
      };
      const id = await onCommitStroke(payload);
      recordAdd(id, payload);
      if (id) setSelectedId(id);
    },
    [editor, onUpdateStroke, onCommitStroke, currentUserId, pageNumber, color, baseStrokeWidth, recordAdd, recordHistory]
  );

  const handlePointerUp = useCallback(
    async (e: React.PointerEvent<SVGSVGElement>) => {
      if (!enabled) return;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      const session = xformRef.current;
      if (session) {
        const p = toNorm(e);
        xformRef.current = null;
        setLiveXform(null);
        setLivePointer(null);
        if (dist(session.start, p) > 0.15) await persistXform(session, p);
        drawingRef.current = false;
        return;
      }

      if (activeTool === 'eraser' || editor) {
        drawingRef.current = false;
        return;
      }

      if (activeTool === 'line' && drawingRef.current) {
        const p = toNorm(e);
        if (e.shiftKey) {
          const last = polyPinnedRef.current[polyPinnedRef.current.length - 1];
          if (!last || dist(last, p) >= 0.8) {
            polyPinnedRef.current = [...polyPinnedRef.current, p];
          }
          draftRef.current = polyPinnedRef.current;
          setDraft(polyPinnedRef.current);
          drawingRef.current = true;
          return;
        }
        const start = startRef.current;
        const pinnedCount = polyPinnedRef.current.length;
        const id = await commitLinePoints(p);
        if (!id && pinnedCount <= 1 && isMarkupTapGesture([start || p, p], start)) {
          const hit = hitMarkupAt(start || p);
          if (hit?.id) setSelectedId(hit.id);
        }
        return;
      }

      if (!drawingRef.current) return;
      drawingRef.current = false;
      const points = draftRef.current;
      const start = startRef.current;
      draftRef.current = null;
      startRef.current = null;
      setDraft(null);
      if (!points || !currentUserId) return;

      const sw = resolveMarkupStrokeWidth(activeTool, baseStrokeWidth, scale);

      if (activeTool === 'stamp') {
        const origin = start || points[0];
        if (!origin) return;
        const payload: MarkupCommitPayload = {
          pageNumber,
          tool: 'stamp',
          color,
          strokeWidth: sw,
          points: [origin],
          stampId: selectedStampId,
          stampScale: 1,
          stampRotation: 0,
          text: getFastener(selectedStampId)?.shortLabel,
        };
        const id = await onCommitStroke(payload);
        recordAdd(id, payload);
        if (id) setSelectedId(id);
        return;
      }

      if (activeTool === 'callout') {
        if (points.length < 2 || dist(points[0], points[points.length - 1]) < 1.2) return;
        openEditor({
          tool: 'callout',
          points,
          text: '',
          screen: screenForPoint(points[points.length - 1]),
        });
        return;
      }

      if (activeTool === 'cloud') {
        if (points.length < 2 || dist(points[0], points[points.length - 1]) < 1.2) {
          if (isMarkupTapGesture(points, start)) {
            const hit = hitMarkupAt(start || points[0]);
            if (hit?.id) setSelectedId(hit.id);
          }
          return;
        }
        const payload: MarkupCommitPayload = {
          pageNumber,
          tool: 'cloud',
          color,
          strokeWidth: sw,
          points: [points[0], points[points.length - 1]],
        };
        const id = await onCommitStroke(payload);
        recordAdd(id, payload);
        if (id) setSelectedId(id);
        return;
      }

      if (activeTool === 'typewriter') {
        const origin = start || points[0];
        if (!origin) return;
        if (isMarkupTapGesture(points, start)) {
          const hit = hitMarkupAt(origin);
          if (hit?.id) {
            if (hit.tool === 'typewriter' && hit.id) {
              setSelectedId(hit.id);
              openEditor({
                tool: 'typewriter',
                points: hit.points,
                text: hit.text || '',
                strokeId: hit.id,
                screen: screenForPoint(hit.points[0]),
              });
              return;
            }
            setSelectedId(hit.id);
            return;
          }
        }
        openEditor({
          tool: 'typewriter',
          points: [origin],
          text: '',
          screen: screenForPoint(origin),
        });
        return;
      }

      if (isMarkupTapGesture(points, start)) {
        const hit = hitMarkupAt(start || points[0]);
        if (hit?.id) setSelectedId(hit.id);
        return;
      }
      if (points.length < 2) return;
      const tool = activeTool === 'highlighter' ? 'highlighter' : 'pen';
      const payload: MarkupCommitPayload = {
        pageNumber,
        tool,
        color,
        strokeWidth: sw,
        points,
      };
      const id = await onCommitStroke(payload);
      recordAdd(id, payload);
    },
    [
      enabled,
      activeTool,
      editor,
      currentUserId,
      color,
      baseStrokeWidth,
      scale,
      pageNumber,
      onCommitStroke,
      recordAdd,
      selectedStampId,
      screenForPoint,
      openEditor,
      persistXform,
      toNorm,
      commitLinePoints,
      hitMarkupAt,
    ]
  );

  const handleShapeDoubleClick = useCallback(
    (stroke: DocumentMarkupStroke) => {
      if (!enabled || !onUpdateStroke || !stroke.id) return;
      if (stroke.tool !== 'callout' && stroke.tool !== 'stamp' && stroke.tool !== 'typewriter') return;
      if (!canEditStroke(stroke)) return;
      const box = stroke.tool === 'typewriter' ? stroke.points[0] : stroke.points[stroke.points.length - 1] || stroke.points[0];
      openEditor({
        tool: stroke.tool === 'typewriter' ? 'typewriter' : 'callout',
        points: stroke.points,
        text: stroke.text || '',
        strokeId: stroke.id,
        screen: screenForPoint(box),
      });
    },
    [enabled, onUpdateStroke, canEditStroke, screenForPoint, openEditor]
  );

  useEffect(() => {
    if (!enabled || activeTool !== 'line') clearLineDraft();
  }, [enabled, activeTool, clearLineDraft]);

  useEffect(() => {
    if (!enabled) return;

    const applyUndo = async () => {
      const op = undoRef.current.pop();
      if (!op) return;
      replayRef.current = true;
      try {
        if (op.type === 'add') {
          await deleteRef.current(op.id);
          if (selectedIdRef.current === op.id) setSelectedId(null);
        } else if (op.type === 'remove') {
          const nextId = await commitRef.current(op.payload);
          adoptRestoredId(op, nextId);
          if (op.payload.tool === 'stamp') setSelectedId(op.id);
        } else {
          await updateRef.current?.(op.id, op.before);
        }
        redoRef.current.push(op);
      } finally {
        replayRef.current = false;
      }
    };

    const applyRedo = async () => {
      const op = redoRef.current.pop();
      if (!op) return;
      replayRef.current = true;
      try {
        if (op.type === 'add') {
          const nextId = await commitRef.current(op.payload);
          adoptRestoredId(op, nextId);
          if (op.payload.tool === 'stamp') setSelectedId(op.id);
        } else if (op.type === 'remove') {
          await deleteRef.current(op.id);
          if (selectedIdRef.current === op.id) setSelectedId(null);
        } else {
          await updateRef.current?.(op.id, op.after);
        }
        pushMarkupHistory(undoRef.current, op);
      } finally {
        replayRef.current = false;
      }
    };

    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)) return;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.stopPropagation();
        if (e.shiftKey) void applyRedo();
        else void applyUndo();
        return;
      }

      if (e.key === 'Escape') {
        if (drawingRef.current && polyPinnedRef.current.length > 0) {
          e.preventDefault();
          polyPinnedRef.current = [];
          drawingRef.current = false;
          draftRef.current = null;
          startRef.current = null;
          setDraft(null);
          return;
        }
        setSelectedId(null);
        return;
      }

      if (e.key === 'Enter' && activeTool === 'line' && drawingRef.current) {
        e.preventDefault();
        const live = draftRef.current?.[draftRef.current.length - 1] || null;
        void commitLinePoints(live);
        return;
      }

      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const id = selectedIdRef.current;
      if (!id) return;
      e.preventDefault();
      e.stopPropagation();
      xformRef.current = null;
      setLiveXform(null);
      setLivePointer(null);
      void removeRecorded(id);
    };

    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled, activeTool, adoptRestoredId, removeRecorded, commitLinePoints]);

  const draftWidth = resolveMarkupStrokeWidth(activeTool, baseStrokeWidth, scale);

  const cursorClass =
    liveXform?.kind === 'rotate' || hoverHandle === 'rotate'
      ? 'markup-layer-svg--rotate'
      : liveXform?.kind === 'scale' || (hoverHandle && hoverHandle !== 'body')
        ? 'markup-layer-svg--resize'
        : liveXform?.kind === 'move' || hoverHandle === 'body'
          ? 'markup-layer-svg--move'
          : activeTool === 'stamp'
            ? 'markup-layer-svg--stamp'
            : activeTool === 'eraser'
              ? 'markup-layer-svg--eraser'
              : activeTool === 'typewriter'
                ? 'markup-layer-svg--typewriter'
                : isEngineeringMarkupTool(activeTool) || activeTool === 'cloud'
                  ? 'markup-layer-svg--engineer'
                  : '';

  return (
    <>
      <svg
        ref={svgRef}
        className={`markup-layer-svg ${enabled ? 'markup-layer-svg--active' : ''} ${cursorClass}`}
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        overflow="visible"
        onPointerDown={e => void handlePointerDown(e)}
        onPointerMove={handlePointerMove}
        onPointerLeave={e => {
          if (e.buttons === 0 && !(activeTool === 'line' && drawingRef.current && polyPinnedRef.current.length >= 2)) {
            void handlePointerUp(e);
          }
        }}
        onPointerUp={e => void handlePointerUp(e)}
      >
        {displayStrokes.map(s =>
          isStoredFreehandTool(s.tool) || s.tool === 'eraser' ? (
            <path
              key={s.id}
              d={s.points.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x} ${pt.y}`).join(' ')}
              stroke={s.color}
              strokeWidth={s.strokeWidth}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
              opacity={s.tool === 'highlighter' ? 0.38 : 1}
              vectorEffect="non-scaling-stroke"
            />
          ) : (
            <MarkupShape
              key={s.id}
              stroke={s}
              scale={pageScale}
              onDoubleClick={enabled ? handleShapeDoubleClick : undefined}
            />
          )
        )}
        {selectedAny && <SelectedMarkupHalo stroke={selectedAny} scale={pageScale} />}
        {selectedStamp && <StampTransformBox stroke={selectedStamp} scale={pageScale} />}
        {draft && draft.length >= 1 && activeTool !== 'eraser' && (
          <DraftMarkupShape
            tool={activeTool === 'highlighter' ? 'highlighter' : activeTool === 'pen' ? 'pen' : activeTool}
            points={draft}
            color={color}
            fill={activeTool === 'callout' ? ENG_CALLOUT_FILL : undefined}
            strokeWidth={draftWidth}
            stampId={selectedStampId}
            stampScale={1}
            stampRotation={0}
            scale={pageScale}
          />
        )}
        {editor && !editor.strokeId && (
          <DraftMarkupShape
            tool={editor.tool}
            points={editor.points}
            color={color}
            fill={editor.tool === 'callout' ? ENG_CALLOUT_FILL : undefined}
            text={editorTextRef.current || editor.text}
            strokeWidth={resolveMarkupStrokeWidth(editor.tool, baseStrokeWidth, scale)}
            scale={pageScale}
          />
        )}
      </svg>
      {editor &&
        createPortal(
          <textarea
            autoFocus
            className={editor.tool === 'typewriter' ? 'markup-typewriter-editor' : 'markup-callout-editor'}
            style={{ left: editor.screen.x, top: editor.screen.y }}
            defaultValue={editor.text}
            placeholder={editor.tool === 'typewriter' ? 'Type a note' : '1.25" HSG'}
            rows={2}
            onPointerDown={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onChange={e => {
              editorTextRef.current = e.target.value;
            }}
            onBlur={e => void finishCalloutEditor(e.target.value, false)}
            onKeyDown={e => {
              e.stopPropagation();
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void finishCalloutEditor((e.target as HTMLTextAreaElement).value, false);
              }
              if (e.key === 'Escape') {
                e.preventDefault();
                void finishCalloutEditor('', true);
              }
            }}
          />,
          document.body
        )}
      {enabled && selectedAny?.id && !editor && (
        createPortal(
          <button
            type="button"
            className="markup-delete-chip"
            style={deleteChipStyle(selectedAny, screenForPoint)}
            title="Delete (Delete)"
            aria-label="Delete markup"
            onPointerDown={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onClick={e => {
              e.stopPropagation();
              void removeRecorded(selectedAny.id!);
            }}
          >
            ×
          </button>,
          document.body
        )
      )}
    </>
  );
}

function deleteChipStyle(
  stroke: DocumentMarkupStroke,
  screenForPoint: (p: DocumentMarkupPoint) => { x: number; y: number }
): { left: number; top: number } {
  const pts = stroke.points;
  if (stroke.tool === 'callout' || stroke.tool === 'typewriter') {
    const box = stroke.tool === 'typewriter' ? pts[0] : pts[pts.length - 1] || pts[0];
    const { w, h } = calloutBoxPx(stroke.text || '');
    const s = screenForPoint(box);
    return { left: s.x + w / 2 + 8, top: s.y - h / 2 };
  }
  if (stroke.tool === 'cloud' && pts.length >= 2) {
    const { x1, y0 } = cloudRect(pts[0], pts[1]);
    const s = screenForPoint({ x: x1, y: y0 });
    return { left: s.x + 12, top: s.y - 8 };
  }
  if (stroke.tool === 'stamp' && pts[0]) {
    const s = screenForPoint(pts[0]);
    const half = stampHalfPx(stroke.stampScale ?? 1);
    return { left: s.x + half + 8, top: s.y - half };
  }
  const last = pts[pts.length - 1] || pts[0];
  const s = screenForPoint(last);
  return { left: s.x + 14, top: s.y - 14 };
}
